/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, IconButton, Paper } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import BoltIcon from '@mui/icons-material/Bolt';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import { Handle, Position } from '@xyflow/react';
import { CustomInput, CustomSwitch, HorizontalNavbar, SidePopover, useAppDispatch, useAppSelector, useEffectOnce } from '../../../globalUtils/globalExports';
import { ExecutionPlan } from './ExecutionPlan';
import CloseIcon from '@mui/icons-material/Close';
import { ICustomCondition } from './CustomConditionComponent';
import { isOperator } from './TriggerPointSidePopover';
import { fetchAdvanceRuleEngineArithmeticOperatorsReferenceData, fetchAdvanceRuleEngineRelationalOperatorReferenceData, fetchAdvanceRuleEngineTriggerParameterReferenceData, getReferenceDataReducer } from '../../../rmsReduxStore/reduxExports';
import CustomConditionSidePopover from './CustomConditionSidePopover';
import { Icon, IconWithTooltip } from '../../../globalUtils/globalIcons';
import { Id, toast } from 'react-toastify';
import { cloneDeep, isInteger, isNil } from 'lodash';
import './IfThenNode.css';
import { parseOptionValues } from './CustomTriggerConditionComponent';

interface CustomNodeProps {
    data:{
        label?: string;
        executionPlan: ExecutionPlan;
        handleUpdateExecutionPlan: Function;
        removeNode: Function;
        parentNodeId: string;
        nodeId: string;
        level: number;
        type: string;
    }
}

const IfThenNode: React.FC<CustomNodeProps> = (props: CustomNodeProps) => {
    const { executionPlan, handleUpdateExecutionPlan, parentNodeId, nodeId, level, type, removeNode } = props.data;
    const dispatch = useAppDispatch();
    const [isIfElseSidePopOverOpen, setIsIfElseSidePopOverOpen] = useState(false);
    const {advanceRuleEngineArithmeticOperatorReferenceData , advanceRuleEngineRelationalOperatorReferenceData, advanceRuleEngineTriggerParameterReferenceData } = useAppSelector(getReferenceDataReducer);
    const [withinDuration,setWithinDuration] = useState<number|null>(null);
    const withinDurationRef = useRef<number | null>(null);
    const [persistenceTime,setPersistenceTime] = useState<number|null>(null);
    const persistenceTimeRef = useRef<number | null>(null);
    const [enableWithinToggle,setEnableWithinToggle] = useState<boolean>(false);
    const [activeTab,setActiveTab] = useState<'within'|'persistence'>('persistence');

    const conditionBlock = useMemo(()=>{
        return executionPlan?.conditionBlocks?.find(item => item?.nodeId === nodeId);
    },[executionPlan?.conditionBlocks,nodeId])

    const tabs = [
        {
        id: 'persistence',
        text: 'Persistence'
        },
        {
        id: 'within',
        text: 'Time Window'
        }
    ]

    const handleTabClick = (val)=>{
        setActiveTab(val);
    }

    const handleEditClick = () => {
        setIsIfElseSidePopOverOpen(true)
    };

    const handleCloseIfElsePopover = () => {
        setIsIfElseSidePopOverOpen(false);
    }

    const errorMessage = (msg: string):Id => toast.error(msg);

    const handleWithinDurationValueChange = (value) => {
        const data = cloneDeep(executionPlan);

        const conditionIdx = data?.conditionBlocks?.findIndex(
            condition => condition?.nodeId === nodeId
        );

        if (conditionIdx === -1) {
            errorMessage('Add conditions before entering within duration.');
            return;
        }

        // Normalize value
        if (value === '' || value == null) {
            setWithinDuration(null);
            withinDurationRef.current = null;

            data.conditionBlocks[conditionIdx].withinDuration = null;
            handleUpdateExecutionPlan(data);
            return;
        }

        const stringValue = String(value).trim();
        const numValue = Number(stringValue);

        // Validate number
        if (isNaN(numValue) || numValue < 0) {
            errorMessage('Within Duration must be a positive number.');
            return;
        }

        // Validate digits
        const [integerPart, decimalPart] = stringValue.split('.');

        if (integerPart.length > 3) {
            toast.warn('Within Duration cannot exceed 3 digits');
            return;
        }

        if (decimalPart && decimalPart.length > 1) {
            toast.warn('Within Duration can only have one decimal place');
            return;
        }

        // Final update
        setWithinDuration(numValue);
        withinDurationRef.current = numValue;

        data.conditionBlocks[conditionIdx].withinDuration = numValue;
        data.conditionBlocks[conditionIdx].waitTimePeriod = null;
        setPersistenceTime(null);
        persistenceTimeRef.current = null;

        handleUpdateExecutionPlan(data);
    };


    const handlePersistenceValueChange = (value) => {
        const data = cloneDeep(executionPlan);

        const conditionIdx = data?.conditionBlocks?.findIndex(
            condition => condition?.nodeId === nodeId
        );

        if (conditionIdx === -1) {
            errorMessage('Add conditions before entering persistence value.');
            return;
        }

        // Normalize value
        if (value === '' || value == null) {
            setPersistenceTime(null);
            persistenceTimeRef.current = null;

            data.conditionBlocks[conditionIdx].waitTimePeriod = null;
            handleUpdateExecutionPlan(data);
            return;
        }

        const stringValue = String(value).trim();
        const numValue = Number(stringValue);

        // Validate number
        if (isNaN(numValue) || numValue < 0) {
            errorMessage('Persistence time must be a positive number.');
            return;
        }

        // Validate digits
        const [integerPart, decimalPart] = stringValue.split('.');

        if (integerPart.length > 3) {
            toast.warn('Persistence time cannot exceed 3 digits');
            return;
        }

        if (decimalPart && decimalPart.length > 1) {
            toast.warn('Persistence time can only have one decimal place');
            return;
        }

        // Final update
        setPersistenceTime(numValue);
        persistenceTimeRef.current = numValue;

        data.conditionBlocks[conditionIdx].waitTimePeriod = numValue;
        data.conditionBlocks[conditionIdx].withinDuration = null;
        setWithinDuration(null);
        withinDurationRef.current = null;

        handleUpdateExecutionPlan(data);
    }

    const operatorRef = useMemo(()=>{
          const arithmeticOperators = advanceRuleEngineArithmeticOperatorReferenceData ?? [];
          const relationalOperators = advanceRuleEngineRelationalOperatorReferenceData ?? [];
          return [...relationalOperators,...arithmeticOperators];
      },[advanceRuleEngineArithmeticOperatorReferenceData,advanceRuleEngineRelationalOperatorReferenceData])


    const getCondition = (label:string) => {
    
        const condition: ICustomCondition = conditionBlock?.conditions?.find(item=>item.condName === label) as ICustomCondition;
        const parameter = advanceRuleEngineTriggerParameterReferenceData?.find(item => item.parameterKey === condition?.paramId);
        const enumValues = condition?.isParamTypeEnum ? parseOptionValues(parameter?.possibleValue ?? null, parameter?.numberKeyFormat ?? '') : [];

        if(condition?.isDerived) {
            return  <div className='trigger-condition-container'>
                <span className='trigger-condition-normal'>{'Derived Condition ('}</span>
                <span className='trigger-condition-bold'>&quot;{condition?.paramLabel}&quot;</span>
                <span className='trigger-condition-normal'>{')'}</span>
            </div>
        }

        const value = condition?.arithmeticParamId ? <>
            <span className='trigger-condition-bold'>&quot;{condition?.arithmeticParamLabel}&quot;</span>
            <span className='trigger-condition-normal'>{condition?.arithmeticOperator}</span>
            <span className='trigger-condition-bold'>{condition?.arithmeticParamValue}</span>
            { condition?.arithmeticParamUnit && <span className='trigger-condition-bold'>{condition?.arithmeticParamUnit}</span> }
        </>
        : condition?.isParamTypeEnum ? <span className='trigger-condition-bold'>{'('}{enumValues?.filter(item=>condition?.paramEnumValue?.includes(item?.value))?.map(item=>item?.label).join(', ')}{')'}</span>
        : condition?.isParamTypeDate && isInteger(Number(condition?.parameter1Value)) ? <span className='trigger-condition-bold'>{'( Last '}{condition?.parameter1Value}{' days )'}</span>
        : condition?.parameter2Value ? 
            <>
            <span className='trigger-condition-bold'> {'('} {condition?.parameter1Value} , {condition?.parameter2Value} {')'} </span>
            { condition?.paramUnit && <span className='trigger-condition-bold'>{condition?.paramUnit}</span> }
            </>
        :<>
            <span className='trigger-condition-bold'>{condition?.parameter1Value}</span>
            { condition?.paramUnit && <span className='trigger-condition-bold'>{condition?.paramUnit}</span> }
        </>

        
        
        return <div className='trigger-condition-container'>
            <span className='trigger-condition-normal'>{'('}</span>
            <span className='trigger-condition-bold'>&quot;{condition?.paramLabel}&quot;</span>
            <span className='trigger-condition-normal'>{operatorRef?.find(item => item?.id === condition?.operator)?.label}</span>
            {value}
            <span className='trigger-condition-normal'>{')'}</span>
        </div>
    }


    const parseExpression = useCallback(()=>{
        const tokens = conditionBlock?.expression?.split(' ');
        const updatedTokens = tokens?.map(token => {
            return isOperator(token) ? <span className='trigger-condition-normal'>{token}</span> : getCondition(token);
        })
        return updatedTokens;
    },[conditionBlock,operatorRef])


    useEffect(()=>{
        const withinDurationValue = isNil(conditionBlock?.withinDuration) ? null : Number(conditionBlock?.withinDuration)
        setWithinDuration(withinDurationValue);
        withinDurationRef.current = withinDurationValue;

        const persistenceTimeValue = isNil(conditionBlock?.waitTimePeriod) ? null : Number(conditionBlock?.waitTimePeriod);
        setPersistenceTime(persistenceTimeValue);
        persistenceTimeRef.current = persistenceTimeValue;

        if(!isNil(withinDurationValue) || !isNil(persistenceTimeValue))setEnableWithinToggle(true);
        if(!isNil(withinDurationValue)) setActiveTab('within');
        else setActiveTab('persistence');

    },[conditionBlock?.withinDuration, conditionBlock?.waitTimePeriod])

    useEffectOnce(()=>{
        dispatch(fetchAdvanceRuleEngineArithmeticOperatorsReferenceData());
        dispatch(fetchAdvanceRuleEngineRelationalOperatorReferenceData());
        dispatch(fetchAdvanceRuleEngineTriggerParameterReferenceData());
    })

    const toggleSwitchConfig = {
        checked: enableWithinToggle,
        switchLabelName: null,
        disableSwitchLabelName: null,
        handleClick: () => {
            if(enableWithinToggle){handleWithinDurationValueChange(null);}
            setEnableWithinToggle(prev=>!prev);
        },
        isDisabled: !(conditionBlock?.conditions && conditionBlock?.conditions.length > 0) 
    };

    return (
        <>
            <Paper
                elevation={3}
                sx={{
                    border: '1px solid #8a2be2',
                    borderLeft: '6px solid #8a2be2',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: '500px',
                    backgroundColor: '#fff',
                    position: 'relative',
                }}
            >
                <Handle type="target" className='transparent-handle' position={Position.Top} />
                <Handle type="source" className='transparent-handle' position={Position.Left} id='left'/>

                <div style={{ display: 'flex', flexDirection: 'column', width: '-webkit-fill-available' }}>
                    <div style={{ display: 'flex', marginBottom: '10px', alignItems: 'center' }}>

                        <AccountTreeOutlinedIcon fontSize="small" sx={{ color: 'gray', marginRight: '4px' }} />
                        <Box display="flex" alignItems="center" marginRight="8px">
                            <Typography
                                variant="subtitle2"
                                sx={{ fontSize: '1rem', color: '#666', marginRight: '4px' }}
                            >
                                If
                            </Typography>
                        </Box>
                    </div>

                    <div style={{ display: 'flex', backgroundColor: '#F5F5FF', borderRadius: '10px', minHeight: '25px', padding: '8px'}}>
                        <Box display="flex" alignItems="center" flex="1">
                            <BoltIcon fontSize="small" sx={{ color: '#f0ad4e', marginRight: '4px' }} />
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    color: '#333',
                                }}
                            >
                                {
                                conditionBlock?.conditions && conditionBlock?.conditions?.length > 0 ? (
                                   <div className='advance__rule__engine__custom-condition'>If {' ( '}{parseExpression()}{' ) '}</div>
                                ) 
                                : (
                                    // Fallback content when there's no executionPlan or condition is empty
                                    <div>
                                        ((Parameter),(Operator),(Value))
                                    </div>
                                )}
                            </Typography>
                        </Box>

                        <IconButton
                            size="small"
                            onClick={handleEditClick}
                            sx={{
                                ':hover': {
                                    backgroundColor: 'transparent', // Removes background hover effect
                                },
                            }}

                        >
                            <EditIcon fontSize="small" sx={{ color: '#666' }} />
                        </IconButton>

                        <IconButton
                            size="small"
                            onClick={() => { }}
                            sx={{
                                position: 'absolute',
                                right: '8px',
                                top: '8px',
                            }}
                        >
                            <CloseIcon
                                onClick={() => removeNode(nodeId)}
                                fontSize="small"
                                sx={{ color: '#666' }} />
                        </IconButton>
                    </div>
                    <div className='advance__logic__if__cond__enable__disable__toggle__container'>
                        <div className='advance__logic__enable__disable__toggle__container-label'>
                            <div className='advance__logic__enable__disable__toggle__container-label-left'>
                                <Icon iconClassName='set-parameter-sidebar'/>
                            </div>
                            <div className='advance__logic__enable__disable__toggle__container-label-right'>
                                <div className='advance__logic__enable__disable__toggle__container-label-text'>Advance Logic</div>
                                <IconWithTooltip iconClassName={'info-icon'}  toolTipText={'Time window, Starts when the immediate upstream node is triggered, for the ‘If’ condition to occur and execute the next actions.'}/>
                            </div>
                        </div>
                        <div className='advance__logic__enable__disable__toggle-btn'>
                            <CustomSwitch
                            switchId='advance-logic-enable-switch'
                            switchClassName='advance__logic__enable__switch'
                            checked={toggleSwitchConfig?.checked}
                            handleClick={() => { toggleSwitchConfig?.handleClick?.() }}
                            isDisabled={toggleSwitchConfig?.isDisabled ?? false}
                            customSpacing={0}
                            />
                        </div>
                    </div>
                    {
                        enableWithinToggle && <div className='advance__logic__tabs__container'>
                            <HorizontalNavbar
                                selectedNav={activeTab}
                                onNavClick={(event, val) => { event?.stopPropagation(); handleTabClick(val) }}
                                navList={tabs}
                            />
                        </div>
                    }
                    { 
                        enableWithinToggle && 
                        <div className='advance__rule__engine__if__cond__advance__param__container'>
                            {
                                activeTab === 'within' ?
                                <div className='advance__rule__engine__within__duration__container'>
                                    <div className='advance__rule__engine__within__duration-label'>
                                        <Icon iconClassName={'rule__engine__form__schedule__execution__icon'} />
                                        <span>Condition exists within a time window of</span>
                                    </div>
                                    <div className='advance__rule__engine__within__input__block'>
                                        <CustomInput
                                            inputValue={withinDuration ?? ''}
                                            inputFieldId={'advance__rule__engine__condition__block__within-duration-input'}
                                            className={'advance__rule__engine__condition__block__within-duration-container'}
                                            isRequired={false}
                                            inputType={'number'}
                                            handleInputChange={
                                                (event) => {
                                                    handleWithinDurationValueChange(event?.target?.value);
                                                }
                                            }
                                        />
                                    </div>
                                    <div className='advance__rule__engine__within__duration-label'>
                                        <span>Minutes.</span>
                                    </div>
                                </div>
                                :
                                <div className='advance__rule__engine__within__duration__container'>
                                    <div className='advance__rule__engine__within__duration-label'>
                                        <Icon iconClassName={'rule__engine__form__schedule__execution__icon'} />
                                        <span>Condition exists continuously for</span>
                                    </div>
                                    <div className='advance__rule__engine__within__input__block'>
                                        <CustomInput
                                            inputValue={persistenceTime ?? ''}
                                            inputFieldId={'advance__rule__engine__condition__block__persistence-input'}
                                            className={'advance__rule__engine__condition__block__within-duration-container'}
                                            isRequired={false}
                                            inputType={'number'}
                                            handleInputChange={
                                                (event) => {
                                                    const value = event.target.value
                                                    handlePersistenceValueChange(value);
                                                }
                                            }
                                            handleKeyPress={(e) => {
                                                if (['+', '-', 'e'].includes(e.key)) {
                                                    e.preventDefault();
                                                    toast.warn('Only digits are allowed');
                                                }
                                            }}
                                        />
                                    </div>
                                    <div className='advance__rule__engine__within__duration-label'>
                                        <span>Minutes.</span>
                                    </div>
                                </div>
                            }
                        </div>
                    }
                </div>

            </Paper>
            {isIfElseSidePopOverOpen && <SidePopover
                id='rule__engine__execution__plan__if__then__else__side__popOver'
                open={isIfElseSidePopOverOpen}
                headerText={'Flow Control: If-Then'}
                headerSecondaryText={'Condition'}
                containerClassName='configure__notification__detail__popover'
                handleClose={() => { }}
                popperClassName='rule__engine__if__then__else'
                headerClassName={'rule__engine__header__config'}
                horizontalLinePopover={true}
                isRuleEnginePopOver={true}
                headingIconClassName={'execution_plan_button_define'}
                ruleEngineSidePopoverOnclose={handleCloseIfElsePopover}
                infoTitle={'Statements are executed in the order precedence of AND and then OR logic'}
                // eslint-disable-next-line react/no-children-prop
                children={
                    <CustomConditionSidePopover 
                        executionPlan={executionPlan} 
                        handleUpdateExecutionPlan={handleUpdateExecutionPlan} 
                        handleClose={handleCloseIfElsePopover} 
                        nodeId={nodeId} 
                        level={level} 
                        parentNodeId={parentNodeId}
                        type = {type}
                        withinDuration = {withinDurationRef.current}
                        waitTimePeriod = {persistenceTimeRef.current}
                    />
                }
            />
            }
        </>

    );

};

export default React.memo(IfThenNode);
